import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { TokenContext } from "./contexts";

const TOKEN_KEY = "chess_auth_token";
const IMPERSONATION_TOKEN_KEY = "chess_impersonation_token";
const IMPERSONATION_METADATA_KEY = "chess_impersonation_metadata";
const FIVE_MIN_MS = 5 * 60 * 1000;
const TEN_MIN_MS = 10 * 60 * 1000;
const HTTP_UNAUTHORIZED = 401;

const redirectToLogin = () => {
    const currentPath = globalThis.location.pathname + globalThis.location.search;
    const encodedRedirect = encodeURIComponent(currentPath);
    globalThis.location.href = `/login/?redirect=${encodedRedirect}`;
};

const isLocalStorageAvailable = () => {
    try {
        const testKey = "__storage_test__";
        globalThis.localStorage.setItem(testKey, testKey);
        globalThis.localStorage.removeItem(testKey);
        return true;
    } catch {
        return false;
    }
};

const isTokenValid = token => {
    if (!token) {
        return false;
    }

    try {
        const decodedToken = jwtDecode(token);
        const currentTime = Math.floor(Date.now() / 1000);
        return decodedToken.exp && decodedToken.exp > currentTime;
    } catch (error) {
        console.info("Ошибка декодирования JWT:", error);
        return false;
    }
};

const extractTokenFromHash = () => {
    const hash = globalThis.location.hash;
    if (hash?.includes("#token=")) {
        const token = hash.split("#token=")[1];
        globalThis.history.replaceState(
            null,
            null,
            globalThis.location.pathname + globalThis.location.search,
        );
        return token;
    }
    return null;
};

export const TokenProvider = ({ children }) => {
    const localStorageAvailable = isLocalStorageAvailable();
    const [token, setToken] = useState(() => {
        const tokenFromHash = extractTokenFromHash();
        if (tokenFromHash && isTokenValid(tokenFromHash)) {
            if (localStorageAvailable) {
                globalThis.localStorage.setItem(TOKEN_KEY, tokenFromHash);
            }
            return tokenFromHash;
        }

        const savedToken = localStorageAvailable ? globalThis.localStorage.getItem(TOKEN_KEY) : null;
        if (savedToken && isTokenValid(savedToken)) {
            return savedToken;
        }

        return "";
    });

    // Impersonation token state - separate from main auth token
    const [impersonationToken, setImpersonationToken] = useState(() => {
        const saved = localStorageAvailable ? globalThis.localStorage.getItem(IMPERSONATION_TOKEN_KEY) : null;
        if (saved && isTokenValid(saved)) {
            return saved;
        }
        return "";
    });

    const [impersonationMetadata, setImpersonationMetadata] = useState(() => {
        try {
            const saved = localStorageAvailable ? globalThis.localStorage.getItem(IMPERSONATION_METADATA_KEY) : null;
            if (!saved) {
                return null;
            }
            
            const metadata = JSON.parse(saved);
            // Validate that metadata's token expiry hasn't passed
            if (metadata.expiresAt && new Date(metadata.expiresAt) > new Date()) {
                return metadata;
            }
            
            // Metadata expired, clean up
            if (localStorageAvailable) {
                globalThis.localStorage.removeItem(IMPERSONATION_METADATA_KEY);
                globalThis.localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
            }
            return null;
        } catch (error) {
            console.info("Error parsing impersonation metadata:", error);
            return null;
        }
    });

    const clearToken = useCallback(() => {
        if (localStorageAvailable) {
            globalThis.localStorage.removeItem(TOKEN_KEY);
        }
        setToken("");
    }, [localStorageAvailable]);

    const clearImpersonationToken = useCallback(() => {
        if (localStorageAvailable) {
            globalThis.localStorage.removeItem(IMPERSONATION_TOKEN_KEY);
            globalThis.localStorage.removeItem(IMPERSONATION_METADATA_KEY);
        }
        setImpersonationToken("");
        setImpersonationMetadata(null);
    }, [localStorageAvailable]);

    const { isLoading: tokenLoading, data: fetchedToken, error: tokenError } = useQuery({
        queryKey: ["auth-token"],
        queryFn: async () => {
            if (import.meta.env.DEV) {
                const devToken = import.meta.env.VITE_API_TOKEN;
                if (devToken && isTokenValid(devToken)) {
                    return devToken;
                }
            }

            // Intentional direct fetch: useApi depends on TokenContext, so bootstrapping
            // token through useApi would create an initialization cycle.
            const res = await fetch(`${import.meta.env.VITE_API_URL}/token/`, {
                headers: {
                    Accept: "application/json",
                },
            });
            if (res.status === HTTP_UNAUTHORIZED) {
                redirectToLogin();
                throw new Error("Unauthorized");
            }

            const data = await res.json();
            if (!data.token) {
                throw new Error("Токен не получен от сервера");
            }
            return data.token;
        },
        enabled: !token && !globalThis.location.pathname.startsWith("/login"),
        staleTime: FIVE_MIN_MS,
        gcTime: TEN_MIN_MS,
    });

    // Adopt a newly fetched token once, during render. Remembering which one was
    // adopted means clearToken() is not undone by the same cached query result.
    const [adoptedToken, setAdoptedToken] = useState(null);
    if (fetchedToken && fetchedToken !== adoptedToken && isTokenValid(fetchedToken)) {
        setAdoptedToken(fetchedToken);
        setToken(fetchedToken);
    }

    useEffect(() => {
        if (adoptedToken && localStorageAvailable) {
            globalThis.localStorage.setItem(TOKEN_KEY, adoptedToken);
        }
    }, [adoptedToken, localStorageAvailable]);

    useEffect(() => {
        if (!tokenError) {
            return;
        }
        toast.error(`Ошибка получения токена: ${tokenError.message}`);
        redirectToLogin();
    }, [tokenError]);

    useEffect(() => {
        if (!localStorageAvailable) {
            toast.error("Локальное хранилище (localStorage) недоступно. Установите поддерживаемый веб-браузер и перезагрузите страницу.");
        }
    }, [localStorageAvailable]);

    const value = useMemo(() => ({
        token,
        setToken,
        clearToken,
        tokenLoading,
        localStorageAvailable,
        impersonationToken,
        setImpersonationToken,
        clearImpersonationToken,
        impersonationMetadata,
        setImpersonationMetadata,
    }), [
        token, setToken, clearToken, tokenLoading, localStorageAvailable,
        impersonationToken, setImpersonationToken, clearImpersonationToken,
        impersonationMetadata, setImpersonationMetadata,
    ]);

    return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>;
};

TokenProvider.propTypes = {
    children: PropTypes.node.isRequired,
};
